import React, { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';

import Button from '@atlaskit/button/new';
import { cssMap } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import PageIcon from '@atlaskit/icon/core/page';
import { InlineEditableTextfield } from '@atlaskit/inline-edit';
import { fg } from '@atlaskit/platform-feature-flags';
import { Box, Inline, Stack } from '@atlaskit/primitives/compiled';
import Skeleton from '@atlaskit/skeleton';
import { token } from '@atlaskit/tokens';
import { useAnalytics } from '@atlassian/conversation-assistant-instrumentation/controllers/analytics';
import { type ActionKeys } from '@atlassian/conversation-assistant-message-actions-api/constants';
import { type ActionRendererFor } from '@atlassian/conversation-assistant-message-actions-api/types';
import { useConfluenceEntities } from '@atlassian/conversation-assistant-service/services/confluence/useConfluenceEntities';
import type { ConfluenceSpaceOption } from '@atlassian/rovo-platform-ui-components/base-space-selector/types';
import { ConfluenceSpaceSelector } from '@atlassian/rovo-platform-ui-components/confluence-space-selector';

import { Divider } from '../../common/ui/divider';
import { buildToolFromAction } from '../../common/utils/buildToolFromAction';

import messages from './messages';

const styles = cssMap({
	pageIcon: {
		paddingTop: token('space.025'),
	},
	pageTitleWrapper: {
		flexGrow: 1,
		marginTop: token('space.negative.100'),
	},
	pageTitleReadView: {
		display: 'flex',
		font: token('font.body'),
		maxWidth: '100%',
		wordBreak: 'break-word',
		paddingBlock: token('space.050'),
		paddingInline: token('space.075'),
	},
	spaceSelector: {
		width: '200px',
	},
});

export const DeepResearchActions = ({
	onCustomDataConfirm,
	getReadonly,
	getStatus,
	cloudId,
	aaid,
	actions,
	interactionModeExecuted,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: ActionRendererFor<typeof ActionKeys.DeepResearchCreatePageAction>): JSX.Element => {
	const { sendAnalyticsEvent, trackAIMAUEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const [showPublishUI, setShowPublishUI] = useState(false);
	const firstAction = actions[0];
	const status = getStatus(firstAction);
	const readonly = getReadonly(firstAction);
	// Used to disable export to page button if message action is readonly OR if message action is currently being submitted
	const isDisabled = status === 'started' || readonly;

	return (
		<Stack space="space.200">
			<Box>
				<Button
					testId="dr-confluence-page-create-export-to-page-button"
					appearance="primary"
					isDisabled={isDisabled}
					aria-expanded={showPublishUI}
					onClick={(_, e) => {
						setShowPublishUI(true);

						sendAnalyticsEvent(
							e.update({
								actionSubjectId: 'rovoDRConfluencePageExportToPageButton',
							}),
						);

						trackAIMAUEvent({
							type: 'actioned',
							actionResult: 'exportToPageButtonClicked',
							proactive: false,
							subjectId: 'rovoDRConfluencePageExportToPageButton',
							attributes: {
								...(interactionModeExecuted ? { interactionModeExecuted } : {}),
							},
						});
					}}
				>
					{formatMessage(messages.exportToPageButton)}
				</Button>
			</Box>
			{showPublishUI && !readonly && (
				<DeepResearchPublishUI
					onCustomDataConfirm={onCustomDataConfirm}
					getStatus={getStatus}
					cloudId={cloudId}
					aaid={aaid}
					actions={actions}
					sendMessageConfirmedTools={sendMessageConfirmedTools}
					recipientAgentId={recipientAgentId}
					conversationId={conversationId}
					messageId={messageId}
				/>
			)}
		</Stack>
	);
};

const DeepResearchPublishUI = ({
	onCustomDataConfirm,
	getStatus,
	cloudId,
	aaid,
	actions,
	sendMessageConfirmedTools,
	recipientAgentId,
	conversationId,
	messageId,
}: Pick<
	ActionRendererFor<typeof ActionKeys.DeepResearchCreatePageAction>,
	| 'onCustomDataConfirm'
	| 'getStatus'
	| 'cloudId'
	| 'aaid'
	| 'actions'
	| 'sendMessageConfirmedTools'
	| 'recipientAgentId'
	| 'conversationId'
	| 'messageId'
> & {
	cloudId: string;
	aaid?: string;
}) => {
	const { sendAnalyticsEvent } = useAnalytics();
	const { formatMessage } = useIntl();
	const firstAction = actions[0];
	const status = getStatus(firstAction);
	const [selectedSpace, setSelectedSpace] = useState<ConfluenceSpaceOption | undefined>(undefined);
	const { data } = firstAction;
	const [name, setName] = useState(data.name);
	const { getEntity } = useConfluenceEntities(cloudId);
	const isDisabled = status !== 'idle' || !name || !selectedSpace;
	const isSubmitting = status === 'started';

	useEffect(() => {
		const fetchSpace = async () => {
			if (data.folder) {
				const space = await getEntity(data.folder, 'space');

				if (space) {
					setSelectedSpace({
						label: space.name,
						value: space.id,
						key: space.key,
					});
				}
			}
		};

		fetchSpace();
	}, [data.folder, getEntity]);

	const handleSubmit = useCallback(() => {
		const mergedData = {
			...data,
			name,
			folder: selectedSpace?.value as string,
		};

		if (fg('rovo_tools_resumption_migration_phase2')) {
			const actionableActions = actions.filter((action) => getStatus(action) === 'idle');
			sendMessageConfirmedTools({
				agentId: recipientAgentId,
				conversationId,
				messageSources: {
					interactionSource: 'deepResearchConfluencePageCreateRenderer',
				},
				confirmed_tools: {
					message_id: messageId ?? '',
					// Confirm the submitted action; cancel every other queued action.
					tools: actionableActions.map((action) =>
						action.invocationId === firstAction.invocationId
							? buildToolFromAction(action, 'confirmed', mergedData)
							: buildToolFromAction(action, 'cancelled'),
					),
				},
			});
			return;
		}

		onCustomDataConfirm(firstAction, mergedData);
	}, [
		onCustomDataConfirm,
		data,
		name,
		selectedSpace?.value,
		firstAction,
		actions,
		sendMessageConfirmedTools,
		recipientAgentId,
		conversationId,
		messageId,
		getStatus,
	]);

	return (
		<Stack space="space.200">
			<Inline space="space.100" alignBlock="center">
				<Heading size="xsmall" as="h4">
					{formatMessage(messages.spaceSelectorLabel)}
				</Heading>
				<Box xcss={styles.spaceSelector}>
					{!selectedSpace ? (
						<Skeleton height={30} width={200} borderRadius={token('radius.large')} />
					) : (
						<ConfluenceSpaceSelector
							cloudId={cloudId}
							userId={aaid}
							testId="dr-confluence-page-create-space-selector"
							isDisabled={isDisabled}
							spacing="compact"
							onMenuOpen={() => {
								sendAnalyticsEvent({
									action: 'opened',
									actionSubject: 'dropdown',
									actionSubjectId: 'rovoDRConfluencePageCreateSpaceSelector',
								});
							}}
							isRequired
							onChange={(option: ConfluenceSpaceOption | null) => {
								if (option) {
									setSelectedSpace(option);
									sendAnalyticsEvent({
										action: 'opened',
										actionSubject: 'dropdown',
										actionSubjectId: 'rovoDRConfluencePageCreateSpaceSelector',
									});
								}
							}}
							value={selectedSpace}
						/>
					)}
				</Box>
			</Inline>
			<Inline space="space.025" alignBlock="center" xcss={styles.pageTitleWrapper}>
				<Box xcss={styles.pageIcon}>
					<PageIcon spacing="none" label="" color="var(--ds-icon-brand)" />
				</Box>
				{isDisabled ? (
					<Box xcss={styles.pageTitleReadView}>
						{name || formatMessage(messages.pageTitlePlaceholder)}
					</Box>
				) : (
					<Box xcss={styles.pageTitleWrapper}>
						<InlineEditableTextfield
							testId="dr-page-name-input-field"
							isRequired
							isCompact
							readViewFitContainerWidth
							defaultValue={name}
							label={formatMessage(messages.pageTitleLabel)}
							editButtonLabel={formatMessage(messages.editPageNameButtonLabel)}
							onConfirm={(value) => {
								setName(value);
								sendAnalyticsEvent({
									action: 'edited',
									actionSubject: 'inlineEdit',
									actionSubjectId: 'rovoDRConfluencePageCreateTitle',
								});
							}}
							onCancel={() => {
								sendAnalyticsEvent({
									action: 'cancelled',
									actionSubject: 'inlineEdit',
									actionSubjectId: 'rovoDRConfluencePageCreateTitle',
								});
							}}
							placeholder={formatMessage(messages.pageTitlePlaceholder)}
						/>
					</Box>
				)}
			</Inline>
			<Divider />
			<Box>
				<Button
					appearance="primary"
					isLoading={isSubmitting}
					isDisabled={isDisabled}
					onClick={(_, e) => {
						handleSubmit();
						sendAnalyticsEvent(
							e.update({
								actionSubjectId: 'rovoDRConfluencePageCreateSubmit',
							}),
						);
					}}
				>
					{formatMessage(messages.publishPageButton)}
				</Button>
			</Box>
		</Stack>
	);
};
